/* CDR Reports — analytics charts using recharts and /api/cdr/stats */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
    Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { ChartBar, Clock, Phone, Coins, PhoneSlash, Robot } from '@phosphor-icons/react';

const COLORS = ['#a3e635', '#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#a855f7'];

const Kpi = ({ icon: Icon, label, value, sub, tid }) => (
    <div className="rounded-xl p-5 border" data-testid={tid}
         style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
        <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-[0.2em]"
                  style={{ color: 'var(--text-secondary)' }}>{label}</span>
            <Icon size={16} style={{ color: 'var(--brand)' }} />
        </div>
        <div className="text-3xl font-bold font-mono">{value}</div>
        {sub && <div className="text-xs mt-1" style={{ color: 'var(--text-disabled)' }}>{sub}</div>}
    </div>
);

const CdrReports = () => {
    const [stats, setStats] = useState(null);
    const [days, setDays] = useState(7);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/cdr/stats?days=${days}`)
            .then((r) => setStats(r.data))
            .finally(() => setLoading(false));
    }, [days]);

    const statusData = stats ? Object.entries(stats.by_status || {}).map(([k, v]) => ({ name: k, value: v })) : [];
    const intentData = stats ? Object.entries(stats.ai_intents || {}).map(([k, v]) => ({ name: k, value: v })) : [];

    const fmtDur = (s) => {
        const n = parseInt(s || 0, 10);
        const h = Math.floor(n / 3600);
        const m = Math.floor((n % 3600) / 60);
        if (h) return `${h}h ${m}m`;
        return `${m}m ${n % 60}s`;
    };

    return (
        <div data-testid="cdr-reports">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                        <ChartBar size={28} style={{ color: 'var(--brand)' }} />
                        CDR Reports
                    </h1>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                        Analýza hovorů, abandonment rate, AI intent statistiky
                    </p>
                </div>
                <select
                    data-testid="cdr-period-select"
                    value={days}
                    onChange={(e) => setDays(parseInt(e.target.value, 10))}
                    className="bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-lg
                               px-3 py-2 text-sm font-mono"
                >
                    <option value={1}>Posledních 24h</option>
                    <option value={7}>7 dní</option>
                    <option value={30}>30 dní</option>
                    <option value={90}>90 dní</option>
                </select>
            </div>

            {loading || !stats ? (
                <div className="text-center py-12" style={{ color: 'var(--text-secondary)' }}>
                    Načítám…
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
                        <Kpi icon={Phone} label="Hovory" value={stats.total_calls}
                             tid="cdr-kpi-total" />
                        <Kpi icon={Phone} label="Přijaté" value={stats.answered}
                             sub={`${stats.answer_rate}% answer rate`} tid="cdr-kpi-answered" />
                        <Kpi icon={PhoneSlash} label="Zmeškané" value={stats.missed}
                             sub={`${stats.abandonment_rate}% abandonment`} tid="cdr-kpi-missed" />
                        <Kpi icon={Clock} label="Průměr. délka"
                             value={fmtDur(stats.avg_duration_seconds)} tid="cdr-kpi-avg" />
                        <Kpi icon={Clock} label="Celkový čas"
                             value={fmtDur(stats.total_duration_seconds)} tid="cdr-kpi-total-dur" />
                        <Kpi icon={Coins} label="Náklady"
                             value={`$${stats.total_cost?.toFixed(2) || '0.00'}`} tid="cdr-kpi-cost" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                        <div className="rounded-xl border p-5"
                             style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                            <div className="text-xs uppercase tracking-[0.2em] font-bold mb-4"
                                 style={{ color: 'var(--text-secondary)' }}>Hovory za den</div>
                            <ResponsiveContainer width="100%" height={240}>
                                <LineChart data={stats.by_day}>
                                    <CartesianGrid stroke="#1f2937" />
                                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} />
                                    <YAxis stroke="#94a3b8" fontSize={11} />
                                    <Tooltip contentStyle={{ background: '#0f1419', border: '1px solid #1f2937' }} />
                                    <Line type="monotone" dataKey="count" stroke="#a3e635" strokeWidth={2}
                                          dot={{ fill: '#a3e635', r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="rounded-xl border p-5"
                             style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                            <div className="text-xs uppercase tracking-[0.2em] font-bold mb-4"
                                 style={{ color: 'var(--text-secondary)' }}>Distribuce podle hodin</div>
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={stats.by_hour}>
                                    <CartesianGrid stroke="#1f2937" />
                                    <XAxis dataKey="hour" stroke="#94a3b8" fontSize={11} />
                                    <YAxis stroke="#94a3b8" fontSize={11} />
                                    <Tooltip contentStyle={{ background: '#0f1419', border: '1px solid #1f2937' }} />
                                    <Bar dataKey="count" fill="#a3e635" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                        <div className="rounded-xl border p-5"
                             style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                            <div className="text-xs uppercase tracking-[0.2em] font-bold mb-4"
                                 style={{ color: 'var(--text-secondary)' }}>Status hovorů</div>
                            {statusData.length === 0 ? (
                                <div className="text-center py-8 text-sm"
                                     style={{ color: 'var(--text-disabled)' }}>Žádná data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={240}>
                                    <PieChart>
                                        <Pie data={statusData} cx="50%" cy="50%" outerRadius={80}
                                             dataKey="value" label>
                                            {statusData.map((_, i) => (
                                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={{ background: '#0f1419', border: '1px solid #1f2937' }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        <div className="rounded-xl border p-5"
                             style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                            <div className="text-xs uppercase tracking-[0.2em] font-bold mb-4 flex items-center gap-2"
                                 style={{ color: 'var(--text-secondary)' }}>
                                <Robot size={14} /> AI Intent rozložení
                            </div>
                            {intentData.length === 0 ? (
                                <div className="text-center py-8 text-sm"
                                     style={{ color: 'var(--text-disabled)' }}>
                                    Žádné AI klasifikace zatím
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={240}>
                                    <BarChart data={intentData} layout="vertical">
                                        <CartesianGrid stroke="#1f2937" />
                                        <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                                        <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={11} width={100} />
                                        <Tooltip contentStyle={{ background: '#0f1419', border: '1px solid #1f2937' }} />
                                        <Bar dataKey="value" fill="#a3e635" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="rounded-xl border"
                             style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                            <div className="px-5 py-3 border-b text-xs font-bold uppercase tracking-[0.15em]"
                                 style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}>
                                Top volající
                            </div>
                            <table className="w-full text-sm" data-testid="cdr-top-callers">
                                <tbody>
                                {(stats.top_callers || []).map(([k, v], i) => (
                                    <tr key={i} className="border-t"
                                        style={{ borderColor: 'var(--border-default)' }}>
                                        <td className="px-5 py-2 font-mono">{k}</td>
                                        <td className="px-5 py-2 text-right font-bold">{v}</td>
                                    </tr>
                                ))}
                                {(stats.top_callers || []).length === 0 && (
                                    <tr><td className="px-5 py-6 text-center text-xs"
                                            style={{ color: 'var(--text-disabled)' }}>—</td></tr>
                                )}
                                </tbody>
                            </table>
                        </div>

                        <div className="rounded-xl border"
                             style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                            <div className="px-5 py-3 border-b text-xs font-bold uppercase tracking-[0.15em]"
                                 style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}>
                                Top DID čísla
                            </div>
                            <table className="w-full text-sm" data-testid="cdr-top-dids">
                                <tbody>
                                {(stats.top_dids || []).map(([k, v], i) => (
                                    <tr key={i} className="border-t"
                                        style={{ borderColor: 'var(--border-default)' }}>
                                        <td className="px-5 py-2 font-mono">{k}</td>
                                        <td className="px-5 py-2 text-right font-bold">{v}</td>
                                    </tr>
                                ))}
                                {(stats.top_dids || []).length === 0 && (
                                    <tr><td className="px-5 py-6 text-center text-xs"
                                            style={{ color: 'var(--text-disabled)' }}>—</td></tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CdrReports;
